import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { faBars } from '@fortawesome/pro-light-svg-icons/faBars';
import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import Loader from 'epn-ui/umd/dist/components/Loader';
import useOnclickOutside from 'react-cool-onclickoutside';

import BaseSemanticLink from '@base/BaseSemanticLink';
import useI18n from '@i18n';

import { HeaderNavContext, ResolvedNavItem } from '../../interfaces';
import resolveNav from '../../utils/resolveNav';
import {
    AUTH_BURGER_MAIN_TREE,
    BURGER_AUTH_USER_MENU_TREE,
    GUEST_MAIN_NAV_TREE,
} from '../../config/trees';
import logout from '../../utils/logout';
import IconButton from '../IconButton';
import IconButtonDropdown from '../IconButtonDropdown';

const menuItemClasses = clsx(
    'block py-2 px-4 text-p1 w-full cursor-pointer text-blueGray-500 text-left bg-white hover:bg-blueGray-100 hover:text-blueGray-800',
);

const innerMenuItemClasses = clsx(menuItemClasses, 'px-8');

interface IBurgerMenuProps {
    navContext: HeaderNavContext;
    isLoading?: boolean;
    children?: React.ReactNode;
}

interface ICollapseButtonProps {
    item: ResolvedNavItem;
}

const ItemContent: React.FC<{ item: ResolvedNavItem }> = ({ item }) => {
    const { t } = useI18n('Base');

    return (
        <div className="flex items-center">
            {t(item.labelKey)}
            {item.icon && <img className="ml-2" src={item.icon} alt="" />}
            {!!item.badge && (
                <div className="p-1 ml-1 text-center text-white bg-red-500 px-1.5 min-w-4 text-overline leading-full">
                    {item.badge}
                </div>
            )}
        </div>
    );
};

const BurgerMenuLink: React.FC<{ item: ResolvedNavItem; className?: string }> = ({
    item,
    className = menuItemClasses,
}) => {
    if (item.action === 'logout') {
        return (
            <button type="button" id={item.testId} className={className} onClick={logout}>
                <ItemContent item={item} />
            </button>
        );
    }

    if (!item.href) {
        return (
            <button type="button" id={item.testId} className={className}>
                <ItemContent item={item} />
            </button>
        );
    }

    return (
        <BaseSemanticLink id={item.testId} className={className} to={item.href}>
            <ItemContent item={item} />
        </BaseSemanticLink>
    );
};

const CollapseButton: React.FC<ICollapseButtonProps> = ({ item }) => {
    const [toggled, setToggled] = useState(false);

    return (
        <>
            <button
                type="button"
                id={item.testId}
                className={clsx(menuItemClasses, 'flex items-center')}
                onClick={() => setToggled(!toggled)}
            >
                <span className="flex mr-2">
                    <ItemContent item={item} />
                </span>
                <FontAwesomeIcon
                    icon={faChevronDown}
                    className={clsx('transition-transform duration-200 ease-in-out transform', {
                        'rotate-180': toggled,
                    })}
                />
            </button>
            {toggled &&
                item.children?.map(child => (
                    <BurgerMenuLink
                        key={`${child.id}-mobile-child`}
                        item={child}
                        className={innerMenuItemClasses}
                    />
                ))}
        </>
    );
};

const BurgerMenu: React.FC<IBurgerMenuProps> = ({ children, navContext, isLoading = false }) => {
    const [active, setActive] = useState(false);
    const icon = active ? faTimes : faBars;
    const ref = useOnclickOutside(() => setActive(false));
    const router = useRouter();
    const mainTree = navContext.isAuth ? AUTH_BURGER_MAIN_TREE : GUEST_MAIN_NAV_TREE;
    const items = isLoading ? [] : resolveNav(mainTree, navContext, 'burger', router.pathname);
    const userMenuItems =
        navContext.isAuth && !isLoading
            ? resolveNav(BURGER_AUTH_USER_MENU_TREE, navContext, 'burger', router.pathname)
            : [];

    useEffect(() => {
        const handleRouteChange = () => {
            setActive(false);
        };

        router.events.on('routeChangeStart', handleRouteChange);

        return () => {
            router.events.off('routeChangeStart', handleRouteChange);
        };
    }, [router.events]);

    return (
        <div ref={ref} className="h-full">
            <IconButton
                onClick={() => {
                    setActive(!active);
                }}
                active={active}
                icon={icon}
                id="MenuBurgerButton"
                aria-label="toggle menu"
            />

            {isLoading && active && (
                <IconButtonDropdown className="flex items-center">
                    <Loader />
                </IconButtonDropdown>
            )}

            {!isLoading && active && (
                <IconButtonDropdown className="!h-auto divide-y max-h-[452px] divide-blueGray-100">
                    {children && <div className="py-5">{children}</div>}

                    <div className="py-2.5">
                        {items.map(item =>
                            item.children?.length ? (
                                <CollapseButton key={`${item.id}-mobile`} item={item} />
                            ) : (
                                <BurgerMenuLink key={`${item.id}-mobile`} item={item} />
                            ),
                        )}
                    </div>

                    {!!userMenuItems.length && (
                        <div className="py-2.5">
                            {userMenuItems.map(item => (
                                <div key={`${item.id}-mobile-user-menu`}>
                                    {item.action === 'logout' && (
                                        <div
                                            className="border-t border-blueGray-100"
                                            role="separator"
                                        />
                                    )}
                                    <BurgerMenuLink item={item} />
                                </div>
                            ))}
                        </div>
                    )}
                </IconButtonDropdown>
            )}
        </div>
    );
};

export default BurgerMenu;
